import { StatusBadge } from '@/components/admin/entretiens/status-badge';
import { OriginMark } from '@/components/admin/origin-mark';
import { SESSIONS_TEST_IDS } from '@/components/admin/sessions/contracts';
import { CreateNpcButton } from '@/components/admin/sessions/create-npc-button';
import { ExportMenu } from '@/components/admin/sessions/export-menu';
import { ReplayPill } from '@/components/admin/sessions/replay-pill';
import { ReviewEntryButton } from '@/components/admin/sessions/review-entry-button';
import { HeadNoyauLineView } from '@/components/admin/sessions/walk/head-noyau-line';
import { plural } from '@/components/admin/sessions/walk/walk-labels';
import { VersionPill } from '@/components/admin/versioned/version-pill';
import { REPLAY_HEAD_TAIL, replayHeadLead } from '@/lib/replay-mark';
import { type HeadNoyauTarget, headNoyauLine, headTitleOf } from '@/lib/session-head';
import { interruptionAuthor } from '@/lib/session-interruption';
import {
  sessionDate,
  sessionStatusLabel,
  sessionStatusTone,
  sessionTime,
} from '@/lib/session-labels';
import { PROD_SESSION_LINE, isProdOrigin } from '@/lib/session-origin';
import type { NoyauTrialSummary, SessionDetail } from '@matter_extractor/shared';
import { ActionMenu, Button } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

/**
 * The head of the parcours (§12.2): where one is, what one is reading, and the
 * two gestures of §12.5.
 *
 * ## The breadcrumb points at `/admin/sessions`
 *
 * It was written against `/admin/journal` while the list of §12.1 was still
 * being built in another worktree, and flipped at the integration of the three
 * screens. `/admin/journal` is not gone - it redirects, keeping its `?session=`
 * (§15) - but a breadcrumb that went through a redirection would put one
 * needless entry in the browser's history on every reading.
 *
 * ## « Relecture » comes first, before « Exporter en »
 *
 * The entry to the review (relecture.md §3.2, issue #187) is the one gesture
 * this head gained: a link on a session that is over, a disabled button with its
 * sentence on one that is not, nothing on an experience with no protocol page
 * (`ReviewEntryButton`, which the conversation mounts too).
 *
 * ## « interrompue à 12:12 par alice » (issue #199, interruption.md §4.4)
 *
 * The subtitle says « terminée à » for any session with an end; an interrupted
 * one says whose gesture ended it, the local part of the email (« jeton
 * partagé » as it stands), and at which hour - the hour of the GESTURE, where
 * the duration of the figures runs to the last activity.
 *
 * ## A replay is marked here, and says so in its subtitle (rejeu.md §12, #193)
 *
 * The violet pill « rejeu · strict » comes after the status, and the subtitle
 * reads « rejouée par Claude, sans lien d'entrée » where an ordinary session
 * says « entrée par le lien ... ». The panel « Rejeu par Claude » is NOT drawn
 * here: it stands under this head and above the figures, mounted by the route
 * (`ReplayPanel`), because it is a block of its own and not a line of the head.
 *
 * ## « Créer un NPC » comes after « Exporter en » (npc.md §4.2, §13.3, issue #210)
 *
 * The gesture that turns a finished session into the draft of an NPC (`CreateNpcButton`), between
 * the export and « Supprimer ». The screen says whether the NPC exist on this server (`createNpc`:
 * the replay open and not the vitrine) - without them nothing is drawn - and the button words its
 * own greyed states from the detail.
 *
 * ## The person's name leads the title, and the noyau has its line (#218)
 *
 * « Camille · Accueil PMS »: the name of the person in bold, the name of the
 * experience after it in normal weight, when the frozen experience marks a field
 * « Nom de la personne dans Sessions » and the session filled it - the very value
 * of the list's line (`session.personName`), whole. The breadcrumb follows. Right
 * under the line of the title comes the line « Noyau retenu » (or « Lu à blanc »),
 * before the frozen settings: `HeadNoyauLineView`, whose words are `lib/session-head.ts`'s
 * and whose pill asks the route (`onOpenNoyau`) to lead to the block. `trials` is the
 * list of the trials of the session, which the route reads only when the line is a
 * trial and which lends the notes of the principal; absent, the line says no figures.
 *
 * ## « Supprimer » lives in the menu « ⋯ » (issue #225)
 *
 * The row of gestures holds what an operator comes for (« Relecture », « Exporter en », « Créer
 * un NPC »); the gestures that are real but rare fold behind a 44 px button named « Plus d'actions »
 * (`ActionMenu`, the same component as the cards): « Copier l'identifiant » - the id of the session,
 * which an operator quotes to Claude or to a colleague - then « Supprimer la session... » in red,
 * which opens the SAME box as before (`onDelete`). A session already in the trash keeps its menu
 * for the identifier alone: `trashSession` is idempotent, so the deletion is not offered a second
 * time, and the banner's « Restaurer » stays the one gesture of the trash. The vitrine shows what
 * it showed: the menu is the head's, whichever instance serves it.
 *
 * ## Exporting is the menu the conversation carries too
 *
 * « Exporter en » (§12.6, issue #173) is ONE component, `ExportMenu`, mounted
 * here and on the conversation: four entries in two groups, and on a session
 * in the trash a disabled button that says why. Its header argues why it is a
 * disclosure rather than `ActionMenu`.
 */

export interface SessionHeadProps {
  readonly detail: SessionDetail;
  /** Opens the confirmation of §12.5. The box itself belongs to the screen. */
  readonly onDelete: () => void;
  /** Do the NPC exist on this server (the replay open, not the vitrine)? Absent reads « no ». */
  readonly createNpc?: boolean;
  /** The trials of the session, newest first: the notes of the line « Lu à blanc » (#218). */
  readonly trials?: readonly NoyauTrialSummary[];
  /** The pill of the line « Noyau retenu » was pressed: the route leads to the block (#218). */
  readonly onOpenNoyau?: ((target: HeadNoyauTarget) => void) | undefined;
}

/** What the entry « Copier l'identifiant » says after a press, for two seconds. */
type CopyState = 'idle' | 'copied' | 'failed';

export function SessionHead({
  detail,
  onDelete,
  createNpc = false,
  trials = [],
  onOpenNoyau,
}: SessionHeadProps) {
  const { session } = detail;
  const [copy, setCopy] = useState<CopyState>('idle');
  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current !== null) clearTimeout(copyTimer.current);
    },
    [],
  );

  async function copyId(): Promise<void> {
    try {
      await navigator.clipboard.writeText(session.id);
      setCopy('copied');
    } catch {
      // No clipboard, or refused (a plain-HTTP dev stack): the box says so, nothing else changes.
      setCopy('failed');
    }
    if (copyTimer.current !== null) clearTimeout(copyTimer.current);
    copyTimer.current = setTimeout(() => setCopy('idle'), 2000);
  }

  const replay = session.replay ?? null;
  const title = headTitleOf(session);
  const noyauLine = headNoyauLine(detail, trials);
  // « Modèles v5 · Règles v3 » (vitrine.md section 5, issue #192): the versions
  // the session froze at its start. Each is null on a session of before the
  // issue, which froze nothing - and then there is nothing to name.
  const frozenModels = detail.settings?.models ?? null;
  const frozenRules = detail.settings?.rules ?? null;
  // « par alice » on an interrupted session (issue #199, §4.4): the local part of the email.
  const author = interruptionAuthor(session.endedBy);

  return (
    <div className="flex flex-col gap-4">
      <nav aria-label="Fil d’Ariane" className="flex flex-wrap gap-2 text-caption text-gray-600">
        <Link to="/admin/sessions" className="text-bleu-nuit hover:underline">
          Sessions
        </Link>
        <span aria-hidden="true">›</span>
        <span>{title.crumb}</span>
      </nav>

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="min-w-0 break-words font-bold text-bleu-nuit text-h3">
              {title.person === null ? (
                title.experience
              ) : (
                <>
                  <span data-testid={SESSIONS_TEST_IDS.headPerson}>{title.person}</span>
                  <span className="font-normal">{` · ${title.experience}`}</span>
                </>
              )}
            </h1>
            {/* The layer's own pill (§4), not a second one: the two tags carry a
                colour each, and « production v3 » has to read the same here as
                on the card the operator came from. A version nobody can resolve
                any more takes neither colour - it is not a pointer. */}
            {session.experienceVersionNumber === null ? (
              <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 font-semibold text-gray-700 text-caption leading-4">
                version illisible
              </span>
            ) : (
              <VersionPill
                entity="session"
                tag={session.entry.tag}
                version={session.experienceVersionNumber}
              />
            )}
            <StatusBadge
              tone={sessionStatusTone(session.status)}
              data-testid={SESSIONS_TEST_IDS.headStatus}
            >
              {sessionStatusLabel(session.status)}
            </StatusBadge>
            {/* The mark « prod » of a session the synchronisation brought up (vitrine.md §8.2, #192). */}
            {isProdOrigin(session) ? <OriginMark testId={SESSIONS_TEST_IDS.headOrigin} /> : null}
            {replay === null ? null : (
              <ReplayPill mode={replay.mode} testId={SESSIONS_TEST_IDS.replayHeadPill} />
            )}
          </div>
          {noyauLine === null ? null : <HeadNoyauLineView line={noyauLine} onOpen={onOpenNoyau} />}
          {frozenModels === null && frozenRules === null ? null : (
            <p data-testid="session-reglages-figes" className="text-gray-600 text-small">
              {frozenModels === null ? null : (
                <>
                  Modèles <b className="font-semibold text-gray-700">v{frozenModels.number}</b>
                </>
              )}
              {frozenModels !== null && frozenRules !== null ? ' · ' : null}
              {frozenRules === null ? null : (
                <>
                  Règles <b className="font-semibold text-gray-700">v{frozenRules.number}</b>
                </>
              )}
              {' · figés au démarrage de la session'}
            </p>
          )}
          <p className="text-gray-600 text-small">
            Commencée le{' '}
            <b className="font-semibold text-gray-700">{sessionDate(session.startedAt)}</b>
            {session.status === 'interrupted' ? (
              <span data-testid={SESSIONS_TEST_IDS.headInterrupted}>
                , interrompue
                {session.endedAt === null ? null : (
                  <>
                    {' '}
                    à <b className="font-semibold text-gray-700">{sessionTime(session.endedAt)}</b>
                  </>
                )}
                {author === null ? null : (
                  <>
                    {' '}
                    par <b className="font-semibold text-gray-700">{author}</b>
                  </>
                )}
              </span>
            ) : session.endedAt === null ? null : (
              <>
                , terminée à{' '}
                <b className="font-semibold text-gray-700">{sessionTime(session.endedAt)}</b>
              </>
            )}
            {' · '}
            {isProdOrigin(session) ? (
              <>
                <span data-testid={SESSIONS_TEST_IDS.headOriginLine}>{PROD_SESSION_LINE}</span>
                {' · '}
              </>
            ) : null}
            {replay !== null ? (
              <span data-testid={SESSIONS_TEST_IDS.replayHeadLine}>
                <b className="font-semibold text-gray-700">{replayHeadLead(replay.player)}</b>
                {REPLAY_HEAD_TAIL}
              </span>
            ) : session.linkName === null ? (
              'sans lien d’entrée'
            ) : (
              <>
                entrée par le lien <b className="font-semibold text-gray-700">{session.linkName}</b>
              </>
            )}
            {' · '}
            <b className="font-semibold text-gray-700">{plural(detail.pages.length, 'page')}</b>{' '}
            {detail.pages.length >= 2 ? 'traversées' : 'traversée'}
            {session.status === 'finished' ? ', la dernière atteinte' : null}
          </p>
        </div>

        {/* Shrinkable, never `shrink-0`: on its own line under 768 px the gestures wrap inside the
            page. Held at its widest, four of them (« Créer un NPC » the fourth) overflow 375 px. */}
        <div className="flex flex-wrap items-start gap-2">
          <ReviewEntryButton session={session} />
          <ExportMenu sessionId={session.id} trashed={session.deletedAt !== null} />
          <CreateNpcButton detail={detail} available={createNpc} />

          {/* « Plus d'actions » (#225): the identifier, then the deletion - which a session already
              in the trash does not offer again: `trashSession` is idempotent, so a second
              « Supprimer » would change nothing, including the date the banner prints. */}
          <ActionMenu
            trigger={
              <Button
                variant="ghost"
                size="icon"
                icon="more_horiz"
                aria-label="Plus d’actions"
                data-testid={SESSIONS_TEST_IDS.headMenu}
              />
            }
            items={[
              { label: 'Copier l’identifiant', onSelect: () => void copyId() },
              ...(session.deletedAt === null
                ? [{ label: 'Supprimer la session…', destructive: true, onSelect: onDelete }]
                : []),
            ]}
          />
          <output
            data-testid={SESSIONS_TEST_IDS.headCopyStatus}
            data-state={copy}
            className="self-center text-gray-600 text-small empty:hidden"
          >
            {copy === 'copied' ? 'Identifiant copié' : copy === 'failed' ? 'Copie refusée' : null}
          </output>
        </div>
      </div>
    </div>
  );
}
